import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertCircle,
  Plus,
  Edit2,
  Loader2,
  Calendar as CalendarIcon,
  Filter,
  X,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Camera,
  ChevronDown,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';

import { API_BASE, BACKEND_URL } from '../utils/apiClient';

const API = API_BASE;

const STATUS_OPTIONS = [
  { value: 'A_FAIRE', label: 'À faire', color: 'bg-gray-200 text-gray-800' },
  { value: 'EN_COURS', label: 'En cours', color: 'bg-blue-600 text-white' },
  { value: 'FAIT', label: 'Fait', color: 'bg-emerald-600 text-white' },
  { value: 'BLOQUE', label: 'Bloqué', color: 'bg-red-600 text-white' }
];

const PRIORITY_OPTIONS = [
  { value: 'faible', label: 'Faible', color: 'bg-gray-500 text-white' },
  { value: 'moyenne', label: 'Moyenne', color: 'bg-amber-500 text-white' },
  { value: 'haute', label: 'Haute', color: 'bg-red-600 text-white' }
];

const Actions = () => {
  const { getAuthHeaders, isManager, user, isDemoReadonly } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [actions, setActions] = useState([]);
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    site_id: 'all',
    zone_id: 'all',
    status: 'all',
    priority: 'all',
    responsible_id: 'all',
    overdue_only: false
  });
  const [showFilters, setShowFilters] = useState(false);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    zone_id: '',
    responsible_id: '',
    priority: 'moyenne',
    deadline: new Date(),
    status: 'A_FAIRE',
    closing_comment: '',
    after_photo_url: ''
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    // Open action from URL param
    const actionId = searchParams.get('id');
    if (actionId && actions.length > 0) {
      const action = actions.find(a => a.id === actionId);
      if (action) {
        openEdit(action);
      }
    }
  }, [searchParams, actions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.zone_id && filters.zone_id !== 'all') params.zone_id = filters.zone_id;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.priority && filters.priority !== 'all') params.priority = filters.priority;
      if (filters.responsible_id && filters.responsible_id !== 'all') params.responsible_id = filters.responsible_id;
      if (filters.overdue_only) params.overdue_only = true;
      if (filters.site_id && filters.site_id !== 'all') params.site_id = filters.site_id;

      const [actionsRes, sitesRes, zonesRes, usersRes] = await Promise.all([
        axios.get(`${API}/actions`, { params, headers: getAuthHeaders() }),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() })
      ]);
      setActions(actionsRes.data);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validation for closing
      if (form.status === 'FAIT' && !form.closing_comment) {
        toast.error('Commentaire de clôture requis');
        return;
      }

      const payload = {
        ...form,
        responsible_id: form.responsible_id === 'none' ? null : form.responsible_id,
        deadline: form.deadline.toISOString()
      };

      if (editingAction) {
        await axios.put(`${API}/actions/${editingAction.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Action mise à jour');
      } else {
        await axios.post(`${API}/actions`, payload, { headers: getAuthHeaders() });
        toast.success('Action créée');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEdit = (action) => {
    setEditingAction(action);
    setForm({
      title: action.title,
      description: action.description || '',
      zone_id: action.zone_id,
      responsible_id: action.responsible_id || 'none',
      priority: action.priority,
      deadline: new Date(action.deadline),
      status: action.status,
      closing_comment: action.closing_comment || '',
      after_photo_url: action.after_photo_url || ''
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingAction(null);
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      zone_id: '',
      responsible_id: 'none',
      priority: 'moyenne',
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      status: 'A_FAIRE',
      closing_comment: '',
      after_photo_url: ''
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setForm({ ...form, after_photo_url: res.data.url });
      toast.success('Photo ajoutée');
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const downloadPDF = async (tourId) => {
    try {
      const response = await axios.get(`${API}/tours/${tourId}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tour_${tourId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const getZoneName = (zoneId) => zones.find(z => z.id === zoneId)?.name || 'N/A';
  const getUserName = (userId) => users.find(u => u.id === userId)?.name || t('sites.notAssigned');
  const getSiteByZone = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    return sites.find(s => s.id === zone?.site_id)?.name || '';
  };

  // Format action title to display "Non conforme" instead of "NOK"
  const formatActionTitle = (title) => {
    if (!title) return '';
    // Replace "NOK:" or "NOK :" with translated "Non conforme :"
    return title
      .replace(/^NOK\s*:\s*/i, `${t('actions.nonConforming')}: `)
      .replace(/^Non conforme\s*:\s*/i, `${t('actions.nonConforming')}: `);
  };

  const isOverdue = (deadline) => new Date(deadline) < new Date();

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(o => o.value === status);
    return <Badge className={`${opt?.color || 'bg-gray-200'} font-bold shadow-sm`}>{t(`actions.statuses.${status}`) || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const opt = PRIORITY_OPTIONS.find(o => o.value === priority);
    return <Badge className={`${opt?.color || 'bg-gray-500'} font-bold shadow-sm`}>{t(`actions.priorities.${priority}`) || priority?.toUpperCase()}</Badge>;
  };

  const getEscalationBadge = (level) => {
    if (level >= 3) return <Badge className="bg-red-700 text-white font-bold border-2 border-red-900 shadow-md">{t('actions.escalation')} {t('actions.escalationLevels.j7')}</Badge>;
    if (level >= 2) return <Badge className="bg-orange-600 text-white font-bold shadow-md">{t('actions.escalation')} {t('actions.escalationLevels.j3')}</Badge>;
    if (level >= 1) return <Badge className="bg-amber-500 text-white font-bold shadow-md">{t('actions.escalation')} {t('actions.escalationLevels.j1')}</Badge>;
    return null;
  };

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => 
    key !== 'overdue_only' && value !== 'all' && value !== false
  ).length + (filters.overdue_only ? 1 : 0);

  if (loading && actions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-[#0E346E]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="actions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 font-['Barlow_Condensed'] tracking-wide">{t('actions.title')}</h1>
          <p className="text-gray-500 mt-1">{actions.length} {t('actions.actionsCount')} {filters.overdue_only ? t('dashboard.overdue').toLowerCase() : ''}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ExportButtons 
            module="actions" 
            filters={{ 
              zone_id: filters.zone_id !== 'all' ? filters.zone_id : undefined,
              status: filters.status !== 'all' ? filters.status : undefined,
              priority: filters.priority !== 'all' ? filters.priority : undefined
            }}
            language={i18n.language || 'fr'}
          />
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-12 border-2 ${activeFiltersCount > 0 ? 'border-[#0E346E] text-[#0E346E]' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-5 h-5 mr-2" />
            {t('common.filters')} {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </Button>
          {isManager() && !isDemoReadonly() && (
            <Button onClick={openNew} className="h-12 bg-[#0E346E] hover:bg-[#0A2850] shadow-md px-6" data-testid="add-action-btn">
              <Plus className="w-5 h-5 mr-2" />
              {t('actions.newAction')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-blue-200 bg-blue-50/50 slide-up">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Select value={filters.site_id} onValueChange={(v) => setFilters({ ...filters, site_id: v, zone_id: 'all' })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('nav.sites')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.allSites')}</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.zone_id} onValueChange={(v) => setFilters({ ...filters, zone_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actions.zone')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {zones
                    .filter(z => filters.site_id === 'all' || !filters.site_id || z.site_id === filters.site_id)
                    .map((zone) => (
                      <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actions.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{t(`actions.statuses.${opt.value}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actions.priority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{t(`actions.priorities.${opt.value}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.responsible_id} onValueChange={(v) => setFilters({ ...filters, responsible_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actions.responsible')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant={filters.overdue_only ? 'default' : 'outline'}
                onClick={() => setFilters({ ...filters, overdue_only: !filters.overdue_only })}
                className={filters.overdue_only ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                <Clock className="w-4 h-4 mr-2" />
                {t('actions.overdueOnly')}
              </Button>
            </div>

            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-3 text-slate-500"
                onClick={() => setFilters({ site_id: 'all', zone_id: 'all', status: 'all', priority: 'all', responsible_id: 'all', overdue_only: false })}
              >
                <X className="w-4 h-4 mr-1" />
                {t('actions.clearFilters')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions List */}
      {actions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-slate-500">{t('actions.noActions')} {filters.overdue_only ? t('dashboard.overdue').toLowerCase() : ''}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => (
            <Card
              key={action.id}
              className={`cursor-pointer hover:shadow-md transition-all ${
                action.escalation_level >= 3 ? 'escalation-3' :
                action.escalation_level >= 2 ? 'escalation-2' :
                action.escalation_level >= 1 ? 'escalation-1' : ''
              }`}
              onClick={() => openEdit(action)}
              data-testid={`action-card-${action.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="font-semibold text-slate-900 truncate">{formatActionTitle(action.title)}</h3>
                      {getPriorityBadge(action.priority)}
                      {getStatusBadge(action.status)}
                      {isOverdue(action.deadline) && action.status !== 'FAIT' && (
                        <Badge className="bg-red-600 text-white">{t('actions.late')}</Badge>
                      )}
                      {getEscalationBadge(action.escalation_level)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                      <span>{getZoneName(action.zone_id)}</span>
                      <span className="text-slate-300">|</span>
                      <span>{getSiteByZone(action.zone_id)}</span>
                      <span className="text-slate-300">|</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {format(new Date(action.deadline), 'dd/MM/yyyy', { locale: fr })}
                      </span>
                      <span className="text-slate-300">|</span>
                      <span>{getUserName(action.responsible_id)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {action.tour_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); downloadPDF(action.tour_id); }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    )}
                    <Button variant="outline" size="sm">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">
              {editingAction ? t('actions.editAction') : t('actions.newActionTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('actions.actionTitle')} *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Réparer éclairage zone B"
                data-testid="action-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('actions.actionDescriptionPlaceholder')}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('actions.zone')} *</Label>
                <Select value={form.zone_id} onValueChange={(v) => setForm({ ...form, zone_id: v })}>
                  <SelectTrigger data-testid="action-zone-select">
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((zone) => (
                      <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('actions.responsible')}</Label>
                <Select value={form.responsible_id} onValueChange={(v) => setForm({ ...form, responsible_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('sites.notAssigned')}</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('actions.priority')}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{t(`actions.priorities.${opt.value}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('actions.deadline')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.deadline ? format(form.deadline, 'dd/MM/yyyy', { locale: fr }) : t('common.select')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.deadline}
                      onSelect={(date) => setForm({ ...form, deadline: date || new Date() })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {editingAction && (
              <>
                <div className="space-y-2">
                  <Label>{t('actions.status')}</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {form.status === 'FAIT' && (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-4 space-y-4">
                      <div className="space-y-2">
                        <Label>{t('actions.closingComment')} *</Label>
                        <Textarea
                          value={form.closing_comment}
                          onChange={(e) => setForm({ ...form, closing_comment: e.target.value })}
                          placeholder={t('actions.closingCommentPlaceholder')}
                          rows={3}
                          data-testid="closing-comment"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('actions.afterPhoto')} ({t('common.optional')})</Label>
                        {form.after_photo_url ? (
                          <div className="relative">
                            <img
                              src={`${BACKEND_URL}${form.after_photo_url}`}
                              alt={t('actions.afterPhoto')}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <Button
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={() => setForm({ ...form, after_photo_url: '' })}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              onChange={handlePhotoUpload}
                              className="hidden"
                            />
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingPhoto}
                            >
                              {uploadingPhoto ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Camera className="w-4 h-4 mr-2" />
                              )}
                              {t('actions.addPhoto')}
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={handleSave}
              disabled={!form.title || !form.zone_id}
              className="bg-[#0E346E] hover:bg-[#0A2850]"
              data-testid="save-action-btn"
            >
              {editingAction ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Actions;
